import { computeGst, gstSummaryByRate, type GstBreakdown } from './gst.js';
import { allocatePaise, assertPaise, roundToRupee, sumPaise, type Paise } from './paise.js';

/**
 * THE TAX ENGINE. Pure functions, no database, no request — the whole of what a bill comes to.
 *
 * It is written here, in shared code, and not in the invoice service for two reasons. The first
 * is that the browser shows the shop what an invoice will say BEFORE it is issued, and a preview
 * computed by different arithmetic from the document is worse than no preview. The second is
 * that this is the part of the product an accountant will audit: it belongs in a file that can
 * be read end to end and tested exhaustively without booting anything.
 *
 * The single decision that shapes everything below: **GST is optional per shop**. A large share
 * of this market is under the ₹40 lakh threshold or on the composition scheme and must not print
 * tax lines at all — not a zero-rated line, not "GST: ₹0", nothing. So `registered: false` is
 * not "tax at 0%"; it produces a document with no tax fields for the renderer to print, and the
 * renderer is written to refuse to print any.
 *
 * Four rules the numbers obey, each of which has an assertion in `invoice-tax.test.ts`:
 *
 *  1. Every line's `cgst + sgst + igst` equals its tax, and `taxable + tax` equals its total.
 *  2. The document's discount is apportioned across lines so the parts sum back to the discount
 *     exactly — a rounded-per-line apportionment loses or invents a paisa, and that paisa is the
 *     one that stops the printed total from reconciling.
 *  3. A tax-inclusive price is honoured to the paisa: the customer pays the ₹1,000 that was
 *     quoted, and the tax is derived out of it rather than added on top.
 *  4. The printed grand total is a whole number of rupees, with the adjustment shown on its own
 *     "Rounding off" line, so `taxable + tax + rounding = total` is visible on the document.
 */

/** What is being supplied. Stitching is a service (SAC); cloth the shop sold is goods (HSN). */
export const SUPPLY_KINDS = ['service', 'goods'] as const;
export type SupplyKind = (typeof SUPPLY_KINDS)[number];

/** The shop's tax position, as the settings screen holds it. */
export interface TaxProfile {
  /** The master switch. False produces a plain bill with no tax anywhere on it. */
  registered: boolean;
  /** The shop's own two-digit state code — the supplier's state. */
  supplierStateCode: string | null;
  /**
   * Where the supply is treated as made. Null means "the shop's own state", which is the case
   * for essentially every walk-in customer of a tailoring shop.
   */
  placeOfSupplyStateCode: string | null;
  /** True when the prices the shop quotes its customers already contain the tax. */
  pricesIncludeTax: boolean;
}

/** One line as the caller hands it in — before discount, before tax. */
export interface TaxLineInput {
  description: string;
  /** Harmonised code. Null on an unregistered shop's bill, where it must not be printed. */
  hsnSac: string | null;
  kind: SupplyKind;
  /** Garments, or metres of cloth. May be fractional for cloth. */
  quantity: number;
  unitPricePaise: Paise;
  /** Ignored entirely when the shop is not registered. */
  ratePercent: number;
  /** `quantity × unitPrice`, passed in because the caller's own pricing rules decide it. */
  amountPaise: Paise;
}

/** One line as it is printed, and as it is frozen onto the issued document. */
export interface TaxLine {
  description: string;
  hsnSac: string | null;
  kind: SupplyKind;
  quantity: number;
  unitPricePaise: Paise;
  /** `quantity × unitPrice`, before this line's share of the discount. */
  grossPaise: Paise;
  /** This line's share of the document-level discount. */
  discountPaise: Paise;
  /** What the customer pays for this line: gross − discount, tax included or not per the shop. */
  netPaise: Paise;
  ratePercent: number;
  taxablePaise: Paise;
  cgstPaise: Paise;
  sgstPaise: Paise;
  igstPaise: Paise;
  taxPaise: Paise;
  /** taxable + tax. Equals `netPaise` exactly when the shop quotes tax-inclusive prices. */
  totalPaise: Paise;
}

/** One row of the rate-wise summary a tax invoice must print. */
export interface TaxRateSummaryRow {
  ratePercent: number;
  taxablePaise: Paise;
  cgstPaise: Paise;
  sgstPaise: Paise;
  igstPaise: Paise;
  taxPaise: Paise;
  totalPaise: Paise;
}

export interface TaxComputation {
  registered: boolean;
  /** True when the tax is one IGST line rather than a CGST + SGST pair. */
  interState: boolean;
  pricesIncludeTax: boolean;
  lines: TaxLine[];
  /** Sum of the lines before discount. */
  grossPaise: Paise;
  discountPaise: Paise;
  /** What tax was computed on. On an unregistered bill this is simply the amount payable. */
  taxablePaise: Paise;
  cgstPaise: Paise;
  sgstPaise: Paise;
  igstPaise: Paise;
  taxPaise: Paise;
  /** taxable + tax, before the whole-rupee adjustment. */
  beforeRoundingPaise: Paise;
  /** The adjustment, positive or negative, printed on its own line. */
  roundingOffPaise: Paise;
  /** What the customer pays. Always a whole number of rupees. */
  totalPaise: Paise;
  /** Empty when the shop is not registered — there is nothing to summarise. */
  rateSummary: TaxRateSummaryRow[];
}

export interface ComputeInvoiceTaxInput {
  lines: readonly TaxLineInput[];
  /** The order's discount, apportioned across the lines below. */
  discountPaise?: Paise;
  profile: TaxProfile;
}

/**
 * Intra-state or inter-state.
 *
 * The default is intra-state, and deliberately: a tailoring shop's customer is standing at its
 * counter. IGST applies only when the place of supply is a DIFFERENT state from the shop's own,
 * and both codes have to be known for that comparison to mean anything — an unknown place of
 * supply is the shop's own state, not a reason to guess at inter-state tax.
 */
export function isInterStateSupply(profile: TaxProfile): boolean {
  if (!profile.registered) return false;
  const supplier = profile.supplierStateCode;
  const place = profile.placeOfSupplyStateCode;
  if (!supplier || !place) return false;
  return supplier !== place;
}

/**
 * The whole document, from its lines.
 *
 * The order of operations matters and is the same order a printed invoice reads in: gross,
 * discount, taxable, tax, rounding, total. Doing the discount after the tax — which is the
 * tempting shortcut, because the discount is a single figure at the bottom of the screen —
 * would tax the customer on money they did not pay.
 */
export function computeInvoiceTax({
  lines,
  discountPaise = 0,
  profile,
}: ComputeInvoiceTaxInput): TaxComputation {
  assertPaise(discountPaise);
  for (const line of lines) assertPaise(line.amountPaise);

  const grossPaise = sumPaise(lines.map((line) => line.amountPaise));
  // A discount cannot exceed the bill: the shop that means to hand money back records a refund
  // (task 17), and an invoice that owes the customer money is not a document that exists.
  const discount = Math.min(Math.max(discountPaise, 0), grossPaise);

  // Largest-remainder, so the shares add back to the discount to the paisa. Weighted by each
  // line's gross, so a ₹100 discount over a ₹3,000 lehenga and a ₹500 blouse falls mostly on
  // the lehenga rather than being split down the middle.
  const shares = allocatePaise(
    discount,
    lines.map((line) => line.amountPaise),
  );

  const interState = isInterStateSupply(profile);
  const inclusive = profile.registered && profile.pricesIncludeTax;

  const breakdowns: GstBreakdown[] = [];
  const computed: TaxLine[] = lines.map((line, index) => {
    const discountShare = shares[index] ?? 0;
    const netPaise = line.amountPaise - discountShare;
    // An unregistered shop has no rate, whatever the caller passed: this is the switch that
    // makes "no tax nomenclature anywhere on the document" true by construction.
    const ratePercent = profile.registered ? line.ratePercent : 0;

    const breakdown = computeGst({
      amountPaise: netPaise,
      ratePercent,
      inclusive,
      interState,
    });
    if (profile.registered) breakdowns.push(breakdown);

    return {
      description: line.description,
      hsnSac: profile.registered ? line.hsnSac : null,
      kind: line.kind,
      quantity: line.quantity,
      unitPricePaise: line.unitPricePaise,
      grossPaise: line.amountPaise,
      discountPaise: discountShare,
      netPaise,
      ratePercent,
      taxablePaise: breakdown.taxablePaise,
      cgstPaise: breakdown.cgstPaise,
      sgstPaise: breakdown.sgstPaise,
      igstPaise: breakdown.igstPaise,
      taxPaise: breakdown.taxPaise,
      totalPaise: breakdown.totalPaise,
    };
  });

  const taxablePaise = sumPaise(computed.map((line) => line.taxablePaise));
  const cgstPaise = sumPaise(computed.map((line) => line.cgstPaise));
  const sgstPaise = sumPaise(computed.map((line) => line.sgstPaise));
  const igstPaise = sumPaise(computed.map((line) => line.igstPaise));
  const taxPaise = sumPaise(computed.map((line) => line.taxPaise));
  const beforeRoundingPaise = sumPaise(computed.map((line) => line.totalPaise));
  const { roundedPaise, roundingOffPaise } = roundToRupee(beforeRoundingPaise);

  return {
    registered: profile.registered,
    interState,
    pricesIncludeTax: inclusive,
    lines: computed,
    grossPaise,
    discountPaise: discount,
    taxablePaise,
    cgstPaise,
    sgstPaise,
    igstPaise,
    taxPaise,
    beforeRoundingPaise,
    roundingOffPaise,
    totalPaise: roundedPaise,
    rateSummary: profile.registered ? summariseByRate(breakdowns) : [],
  };
}

function summariseByRate(breakdowns: readonly GstBreakdown[]): TaxRateSummaryRow[] {
  return gstSummaryByRate(breakdowns).map((row) => ({
    ratePercent: row.ratePercent,
    taxablePaise: row.taxablePaise,
    cgstPaise: row.cgstPaise,
    sgstPaise: row.sgstPaise,
    igstPaise: row.igstPaise,
    taxPaise: row.taxPaise,
    totalPaise: row.totalPaise,
  }));
}

/**
 * The self-check the invoice service runs before it writes a document, and the tests assert
 * directly. A computation that does not reconcile must never reach a customer's hands, and
 * "the printed total always adds up" is worth failing a request over.
 */
export function taxComputationReconciles(computation: TaxComputation): boolean {
  const linesAddUp = computation.lines.every(
    (line) =>
      line.cgstPaise + line.sgstPaise + line.igstPaise === line.taxPaise &&
      line.taxablePaise + line.taxPaise === line.totalPaise &&
      line.grossPaise - line.discountPaise === line.netPaise,
  );

  const discountAddsUp =
    sumPaise(computation.lines.map((line) => line.discountPaise)) === computation.discountPaise;

  const documentAddsUp =
    computation.taxablePaise + computation.taxPaise === computation.beforeRoundingPaise &&
    computation.beforeRoundingPaise + computation.roundingOffPaise === computation.totalPaise &&
    computation.cgstPaise + computation.sgstPaise + computation.igstPaise === computation.taxPaise;

  const summaryAddsUp =
    !computation.registered ||
    (sumPaise(computation.rateSummary.map((row) => row.taxablePaise)) ===
      computation.taxablePaise &&
      sumPaise(computation.rateSummary.map((row) => row.taxPaise)) === computation.taxPaise);

  return linesAddUp && discountAddsUp && documentAddsUp && summaryAddsUp;
}
